import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, Pencil } from "lucide-react";
import { toast } from "sonner";
import { formatRupiah } from "@/components/ReportTables";
import { SearchBar, matchesSearch, usePagination, PaginationNav, PAGE_SIZE } from "@/components/tableUtils";

const EMPTY = { nama_barang: "", jenis: "Pembelian", jumlah: 1, estimasi_harga: "", spesifikasi: "", alasan: "" };

export default function Pengadaan() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY);
  const [search, setSearch] = useState("");
  const filtered = items.filter((p) => matchesSearch(p, ["nama_barang", "jenis", "alasan", "diajukan_oleh"], search));
  const { page, setPage, pageCount } = usePagination(filtered.length);
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const load = () => api.get("/procurements").then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!form.nama_barang) {
      toast.error("Nama barang wajib diisi");
      return;
    }
    setLoading(true);
    try {
      await api.post("/procurements", {
        ...form,
        jumlah: parseInt(form.jumlah) || 1,
        estimasi_harga: parseFloat(form.estimasi_harga) || 0,
      });
      toast.success("Rekomendasi ditambahkan");
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (p) => {
    setEditing(p);
    setEditForm({
      nama_barang: p.nama_barang, jenis: p.jenis, jumlah: p.jumlah,
      estimasi_harga: p.estimasi_harga, spesifikasi: p.spesifikasi, alasan: p.alasan,
    });
    setDialogOpen(true);
  };

  const saveEdit = async () => {
    if (!editForm.nama_barang) {
      toast.error("Nama barang wajib diisi");
      return;
    }
    try {
      await api.put(`/procurements/${editing.id}`, {
        ...editForm,
        jumlah: parseInt(editForm.jumlah) || 1,
        estimasi_harga: parseFloat(editForm.estimasi_harga) || 0,
      });
      toast.success("Rekomendasi diperbarui");
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="pengadaan-page" className="space-y-8">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight">Rekomendasi Aset</h1>
        <p className="text-sm text-muted-foreground">Rekomendasi pembelian dan perbaikan barang untuk keperluan aset IT.</p>
      </div>

      <div className="bg-card border border-border rounded-lg shadow-sm p-6">
        <h2 className="font-heading font-semibold text-lg mb-4">Input Rekomendasi</h2>
        <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Nama Barang</Label>
            <Input value={form.nama_barang} onChange={(e) => setForm({ ...form, nama_barang: e.target.value })} data-testid="pengadaan-form-nama" />
          </div>
          <div className="space-y-2">
            <Label>Jenis</Label>
            <Select value={form.jenis} onValueChange={(v) => setForm({ ...form, jenis: v })}>
              <SelectTrigger data-testid="pengadaan-form-jenis"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Pembelian">Pembelian</SelectItem>
                <SelectItem value="Perbaikan">Perbaikan</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Jumlah</Label>
            <Input type="number" min="1" value={form.jumlah} onChange={(e) => setForm({ ...form, jumlah: e.target.value })} data-testid="pengadaan-form-jumlah" />
          </div>
          <div className="space-y-2">
            <Label>Estimasi Harga (Rp)</Label>
            <Input type="number" min="0" value={form.estimasi_harga} onChange={(e) => setForm({ ...form, estimasi_harga: e.target.value })} data-testid="pengadaan-form-harga" />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label>Spesifikasi</Label>
            <Input value={form.spesifikasi} onChange={(e) => setForm({ ...form, spesifikasi: e.target.value })} data-testid="pengadaan-form-spesifikasi" />
          </div>
          <div className="space-y-2 md:col-span-3">
            <Label>Alasan / Dasar Rekomendasi</Label>
            <Textarea value={form.alasan} onChange={(e) => setForm({ ...form, alasan: e.target.value })} data-testid="pengadaan-form-alasan" />
          </div>
          <div className="md:col-span-3">
            <Button type="submit" disabled={loading} data-testid="pengadaan-form-submit">
              <Plus className="w-4 h-4 mr-2" /> Tambah Rekomendasi
            </Button>
          </div>
        </form>
      </div>

      <SearchBar value={search} onChange={(v) => { setSearch(v); setPage(1); }} testid="pengadaan-search" placeholder="Cari nama barang, jenis, atau pengaju..." />

      <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <h2 className="font-heading font-semibold text-lg">Daftar Rekomendasi</h2>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Barang</TableHead>
              <TableHead>Jenis</TableHead>
              <TableHead>Jumlah</TableHead>
              <TableHead>Estimasi Harga</TableHead>
              <TableHead>Alasan</TableHead>
              <TableHead>Diajukan Oleh</TableHead>
              <TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paged.map((p) => (
              <TableRow key={p.id} data-testid={`pengadaan-row-${p.id.slice(0, 8)}`}>
                <TableCell className="font-medium">{p.nama_barang}</TableCell>
                <TableCell>{p.jenis}</TableCell>
                <TableCell>{p.jumlah}</TableCell>
                <TableCell>{formatRupiah(p.estimasi_harga)}</TableCell>
                <TableCell className="max-w-xs truncate">{p.alasan || "—"}</TableCell>
                <TableCell>{p.diajukan_oleh}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(p)} title="Edit" data-testid={`edit-pengadaan-${p.id.slice(0, 8)}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground py-10" data-testid="pengadaan-empty">
                  Belum ada rekomendasi pengadaan.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <PaginationNav page={page} pageCount={pageCount} onChange={setPage} testid="pengadaan-pagination" />
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="pengadaan-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit Rekomendasi</DialogTitle>
            <DialogDescription>Perbarui data rekomendasi pembelian/perbaikan.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2 col-span-2">
              <Label>Nama Barang</Label>
              <Input value={editForm.nama_barang} onChange={(e) => setEditForm({ ...editForm, nama_barang: e.target.value })} data-testid="pengadaan-edit-nama" />
            </div>
            <div className="space-y-2">
              <Label>Jenis</Label>
              <Select value={editForm.jenis} onValueChange={(v) => setEditForm({ ...editForm, jenis: v })}>
                <SelectTrigger data-testid="pengadaan-edit-jenis"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Pembelian">Pembelian</SelectItem>
                  <SelectItem value="Perbaikan">Perbaikan</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Jumlah</Label>
              <Input type="number" min="1" value={editForm.jumlah} onChange={(e) => setEditForm({ ...editForm, jumlah: e.target.value })} data-testid="pengadaan-edit-jumlah" />
            </div>
            <div className="space-y-2">
              <Label>Estimasi Harga (Rp)</Label>
              <Input type="number" min="0" value={editForm.estimasi_harga} onChange={(e) => setEditForm({ ...editForm, estimasi_harga: e.target.value })} data-testid="pengadaan-edit-harga" />
            </div>
            <div className="space-y-2">
              <Label>Spesifikasi</Label>
              <Input value={editForm.spesifikasi} onChange={(e) => setEditForm({ ...editForm, spesifikasi: e.target.value })} data-testid="pengadaan-edit-spesifikasi" />
            </div>
            <div className="space-y-2 col-span-2">
              <Label>Alasan / Dasar Rekomendasi</Label>
              <Textarea value={editForm.alasan} onChange={(e) => setEditForm({ ...editForm, alasan: e.target.value })} data-testid="pengadaan-edit-alasan" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="pengadaan-edit-cancel">Batal</Button>
            <Button onClick={saveEdit} data-testid="pengadaan-edit-save">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
